<template>
    <div data-component="AmsSidefilter">
        <cs-accordion id="price-sidefilter" isInitiallyOpen hasBorder label="Preis in Euro">
            <ams-min-max-filter
                v-model="priceModel"
                :minLabel="priceMinLabel"
                :formatter="priceFormatter"
                :options="priceOptions"
                data-mod="priceFilter"
            />
        </cs-accordion>

        <cs-accordion id="registrationDate-sidefilter" isInitiallyOpen hasBorder label="Erstzulassung">
            <ams-min-max-filter
                v-model="registrationDateModel"
                :formatter="registrationDateFormatter"
                :options="registrationDateOptions"
                data-mod="registrationDateFilter"
            />
        </cs-accordion>

        <cs-accordion id="mileage-sidefilter" isInitiallyOpen hasBorder label="Kilometerstand">
            <ams-min-max-filter
                v-model="mileageModel"
                :formatter="mileageFormatter"
                :options="mileageOptions"
                data-mod="mileageFilter"
            />
        </cs-accordion>

        <cs-accordion id="deliveryOption-sidefilter" isInitiallyOpen hasBorder label="Kauf- und Lieferoptionen">
            <ams-delivery-options
                v-model="deliveryOptionModel"
                radioButtonName="delivery-type-sidefilter"
                :onlinePurchaseLink="store.onlinePurchaseLink"
            />
        </cs-accordion>

        <cs-accordion id="location-sidefilter" isInitiallyOpen hasBorder label="Ort und Umkreis">
            <cs-location-autocomplete
                :key="initialLocation"
                label="PLZ oder Ort"
                hasGermanyFallback
                :initialValue="initialLocation"
                @select="onLocationSelect"
            />
            <cs-dropdown
                v-model="radiusModelValue"
                data-jest="radius-dropdown"
                class="mt-16"
                label="Umkreis"
                :noEmptyOption="radiusNoEmptyOption"
                :options="radiusOptions"
                :isDisabled="radiusIsDisabled"
            />
        </cs-accordion>
        <cs-accordion
            v-for="(filter, i) in filters"
            :id="determineHeadlineBy(filter.key) + '-sidefilter'"
            :key="filter.key"
            :isInitiallyOpen="i === 0"
            hasBorder
            :label="determineHeadlineBy(filter.key)"
        >
            <ams-vehicle-features
                :filter="filter"
                checkboxName="sidefilter"
                :modelValue="filter.getManagedData()"
                @update:modelValue="filter.setManagedData($event)"
            />
        </cs-accordion>
        <cs-accordion id="enginePower-sidefilter" hasBorder label="Leistung">
            <ams-engine-power v-model="enginePowerModel" />
        </cs-accordion>
        <cs-accordion id="seatsAndDoors-sidefilter" hasBorder label="Sitze und Türen">
            <cs-dropdown v-model="seatsModelValue" label="Sitze" :options="seatsOptions" />
            <cs-dropdown v-model="doorsModelValue" class="mt-16" label="Türen" :options="doorsOptions" />
        </cs-accordion>
    </div>
</template>

<script lang="ts" setup>
import { CsAccordion, CsDropdown, CsLocationAutocomplete } from '@sunrise/components'
import { omit } from 'lodash-es'
import { useDeliveryOptions } from '../../composables/filters/deliveryOptions/deliveryOptions'
import { useLocation } from '../../composables/filters/location/location'
import { useRadius } from '../../composables/filters/radius/radius'
import useManagedData from '../../composables/useManagedData/useManagedData'
import { AmsSearchRequest } from '../../services/amsSearch/amsSearch.interface'
import { useInitializedAmsSearchStore } from '../../store/amsSearch'
import AmsMinMaxFilter, { usePrice } from '../AmsMinMaxFilter'
import { useMileage } from '../AmsMinMaxFilter/composables/useMileage'
import { useRegistrationDate } from '../AmsMinMaxFilter/composables/useRegistrationDate'
import AmsEnginePower from '../AmsEnginePower'
import { useEnginePower } from '../../composables/filters/enginePower/enginePower'
import AmsDeliveryOptions from '../filters/AmsDeliveryOptions/AmsDeliveryOptions.vue'
import AmsVehicleFeatures from '../AmsVehicleFeatures/AmsVehicleFeatures.vue'
import { computed } from 'vue'
import { managedFilters, determineHeadlineBy } from '../AmsVehicleFeatures/vehicleFeaturesHelper'
import { useDoors } from '../../composables/filters/doors'
import { useSeats } from '../../composables/filters/seats'

const store = useInitializedAmsSearchStore()

const { managedData } = useManagedData(() => omit<AmsSearchRequest, ['page']>(store.currentRequest, 'page'))

const { priceModel, priceMinLabel, priceFormatter, priceOptions } = usePrice(managedData)
const { registrationDateModel, registrationDateFormatter, registrationDateOptions } = useRegistrationDate(managedData)
const { mileageModel, mileageFormatter, mileageOptions } = useMileage(managedData)
const { deliveryOptionModel } = useDeliveryOptions(managedData)
const { onLocationSelect, initialLocation } = useLocation(managedData)
const { radiusIsDisabled, radiusNoEmptyOption, radiusOptions, radiusModelValue } = useRadius(managedData)
const { enginePowerModel } = useEnginePower(managedData)
const filters = computed(() => managedFilters(managedData, store.results.filters))
const { doorsModelValue, doorsOptions } = useDoors(managedData)
const { seatsModelValue, seatsOptions } = useSeats(managedData)
</script>
